import React, { useState } from 'react';
import { useGameStore, BotDifficulty } from '../../state/game-store.js';
import { TANK_CLASSES } from '@tankworld/game-assets';
import { soundSystem } from '../../audio/sound-system.js';
import { Shield, Play, Users, Settings as SettingsIcon, Crosshair, Award, LogIn, UserPlus, LogOut, Star, Wrench, Activity } from 'lucide-react';

interface MainMenuProps {
  onOpenMultiplayer: () => void;
  onOpenGarage: () => void;
  onOpenArmorLab: () => void;
  onOpenSettings: () => void;
}

export const MainMenu: React.FC<MainMenuProps> = ({
  onOpenMultiplayer,
  onOpenGarage,
  onOpenArmorLab,
  onOpenSettings,
}) => {
  const setGameMode = useGameStore((s) => s.setGameMode);
  const playerName = useGameStore((s) => s.playerName);
  const setPlayerName = useGameStore((s) => s.setPlayerName);
  const selectedTankClass = useGameStore((s) => s.selectedTankClass);
  const setSelectedTankClass = useGameStore((s) => s.setSelectedTankClass);
  const botDifficulty = useGameStore((s) => s.botDifficulty);
  const setBotDifficulty = useGameStore((s) => s.setBotDifficulty);
  const user = useGameStore((s) => s.user);
  const openAuthModal = useGameStore((s) => s.openAuthModal);
  const logout = useGameStore((s) => s.logout);

  const [activeTab, setActiveTab] = useState<'deploy' | 'hangar' | 'manual'>('deploy');

  const handleLaunchSingleplayer = () => {
    soundSystem.playUiClick();
    setGameMode('singleplayer');
  };

  const handleLaunchMultiplayer = () => {
    soundSystem.playUiClick();
    onOpenMultiplayer();
  };

  const currentTank = TANK_CLASSES[selectedTankClass] || TANK_CLASSES.mbt_standard;

  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-between p-8 bg-gradient-to-b from-zinc-950/90 via-zinc-900/80 to-zinc-950/95 overflow-y-auto">
      {/* Top Banner */}
      <div className="flex flex-wrap justify-between items-center border-b border-zinc-800 pb-4 gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-500" />
            <h1 className="text-3xl font-extrabold tracking-widest text-zinc-100 uppercase">
              TANKWORLD <span className="text-emerald-500">3D</span>
            </h1>
          </div>
          <p className="text-xs text-zinc-400 tracking-wider mt-1 font-mono">
            REALISTIC ARMORED VEHICLE & BALLISTICS SIMULATION ENGINE // v1.0.0
          </p>
        </div>

        {/* Commander Profile / Auth Bar */}
        {user ? (
          <div className="hud-panel px-4 py-2.5 rounded-lg border border-emerald-800/60 flex items-center gap-4 bg-zinc-900/90 shadow-lg">
            <div className="w-10 h-10 rounded-full bg-emerald-950 border-2 border-emerald-500 flex items-center justify-center font-bold text-emerald-400 text-sm shadow-inner">
              {user.username.slice(0, 2).toUpperCase()}
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="text-xs px-1.5 py-0.2 rounded bg-emerald-950/80 border border-emerald-700/60 text-emerald-400 font-mono font-bold uppercase">
                  {user.rank}
                </span>
                <span className="text-sm font-bold text-zinc-100 font-mono tracking-wide">
                  {user.username}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-zinc-400 font-mono">
                <span className="flex items-center gap-1 text-amber-400 font-semibold">
                  <Star className="w-3 h-3 fill-amber-400" /> {user.rating} pts
                </span>
                <span>Hạ gục: <b className="text-zinc-200">{user.totalKills}</b></span>
                <span>Thắng: <b className="text-zinc-200">{user.wins}</b></span>
              </div>
            </div>
            <button
              onClick={logout}
              className="ml-2 p-2 text-zinc-400 hover:text-red-400 rounded-lg hover:bg-zinc-800/80 transition-colors"
              title="Đăng xuất"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="hud-panel px-3 py-1.5 rounded border border-zinc-700/60 flex items-center gap-2 bg-zinc-900/80">
              <div className="w-7 h-7 rounded-full bg-zinc-800 border border-zinc-600 flex items-center justify-center font-bold text-xs text-zinc-300">
                {playerName.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="text-[9px] text-zinc-400 uppercase font-mono">Callsign (Khách)</div>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value.slice(0, 20))}
                  className="bg-transparent border-b border-zinc-700 text-xs font-bold text-zinc-100 focus:outline-none focus:border-emerald-400 font-mono w-28"
                />
              </div>
            </div>

            <button
              onClick={() => openAuthModal('login')}
              className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-bold font-mono tracking-wider border border-zinc-700 transition-all"
            >
              <LogIn className="w-3.5 h-3.5 text-lime-400" />
              ĐĂNG NHẬP
            </button>
            <button
              onClick={() => openAuthModal('register')}
              className="flex items-center gap-1.5 px-3 py-2 bg-lime-600 hover:bg-lime-500 text-zinc-950 rounded-lg text-xs font-bold font-mono tracking-wider shadow-md shadow-lime-900/40 transition-all"
            >
              <UserPlus className="w-3.5 h-3.5" />
              ĐĂNG KÝ
            </button>
          </div>
        )}
      </div>


      {/* Center Layout: Operations / Hangar / Manual */}
      <div className="max-w-5xl mx-auto w-full my-auto py-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left Column: Mission Deployment */}
        <div className="md:col-span-2 space-y-6">
          <div className="hud-panel p-6 rounded-lg border border-emerald-900/40 space-y-4">
            <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase tracking-wider text-sm">
              <Crosshair className="w-4 h-4" /> Operations Command
            </div>

            <div className="space-y-4">
              {/* Singleplayer Card */}
              <div className="p-4 bg-zinc-900/60 rounded border border-zinc-800 hover:border-emerald-700/60 transition-all">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-zinc-100">Proving Grounds (Solo Training)</h3>
                    <p className="text-xs text-zinc-400 mt-1">
                      Engage tactical AI bots in realistic terrain with height variations, obstacles, and vehicle ballistics.
                    </p>
                  </div>
                </div>

                {/* Bot Difficulty Selector */}
                <div className="mt-4 flex items-center gap-3 text-xs font-mono">
                  <span className="text-zinc-400">Bot AI Combat Tier:</span>
                  {(['easy', 'normal', 'hard'] as BotDifficulty[]).map((d) => (
                    <button
                      key={d}
                      onClick={() => setBotDifficulty(d)}
                      className={`px-3 py-1 rounded text-xs font-bold uppercase transition-all ${
                        botDifficulty === d
                          ? 'bg-emerald-600 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleLaunchSingleplayer}
                  className="mt-4 w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold rounded flex items-center justify-center gap-2 text-sm uppercase tracking-wider transition-all"
                >
                  <Play className="w-4 h-4 fill-current" /> Deploy to Training Grounds
                </button>
              </div>

              {/* Multiplayer Card */}
              <div className="p-4 bg-zinc-900/60 rounded border border-zinc-800 hover:border-emerald-700/60 transition-all">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-zinc-100">Multiplayer War Room</h3>
                    <p className="text-xs text-zinc-400 mt-1">
                      Join real-time battles over secure WebSockets with server-authoritative physics, ballistics, and room lobbies.
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleLaunchMultiplayer}
                  className="mt-4 w-full py-3 bg-zinc-800 hover:bg-zinc-700 text-emerald-400 font-bold rounded flex items-center justify-center gap-2 text-sm uppercase tracking-wider border border-emerald-900/60 transition-all"
                >
                  <Users className="w-4 h-4" /> Mở Sảnh Tác Chiến (Combat Lobby)
                </button>
              </div>

              {/* 3D Garage & Armory Card */}
              <div className="p-4 bg-zinc-900/60 rounded border border-zinc-800 hover:border-emerald-700/60 transition-all">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-zinc-100 flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-emerald-400" /> 3D Garage & Kho Trang Bị
                    </h3>
                    <p className="text-xs text-zinc-400 mt-1">
                      Tùy chỉnh module xe tăng, phân bố cơ số đạn, kiểm tra giới hạn tải trọng và bật chế độ bản đồ nhiệt giáp kỹ thuật.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    soundSystem.playUiClick();
                    onOpenGarage();
                  }}
                  className="mt-4 w-full py-2.5 bg-zinc-800 hover:bg-emerald-600 hover:text-zinc-950 text-emerald-400 font-bold rounded flex items-center justify-center gap-2 text-xs uppercase tracking-wider border border-zinc-700 transition-all"
                >
                  <Wrench className="w-4 h-4" /> MỞ 3D GARAGE & KHO VẬT PHẨM
                </button>
              </div>

              {/* Armor Analysis Lab Card */}
              <div className="p-4 bg-zinc-900/60 rounded border border-zinc-800 hover:border-emerald-700/60 transition-all">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-zinc-100 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-cyan-400" /> Phòng Thí Nghiệm Đạn Xuyên Giáp
                    </h3>
                    <p className="text-xs text-zinc-400 mt-1">
                      Mô phỏng quỹ đạo 3D, đo góc tới θ, độ dày hiệu dụng LOS, phản xạ nảy đạn ricochet, xuyên phá composite và xuất dữ liệu JSON.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    soundSystem.playUiClick();
                    onOpenArmorLab();
                  }}
                  className="mt-4 w-full py-2.5 bg-zinc-800 hover:bg-cyan-600 hover:text-zinc-950 text-cyan-400 font-bold rounded flex items-center justify-center gap-2 text-xs uppercase tracking-wider border border-zinc-700 transition-all"
                >
                  <Activity className="w-4 h-4" /> VÀO PHÒNG PHÂN TÍCH GIÁP
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Tank Hangar Selector */}
        <div className="hud-panel p-6 rounded-lg border border-emerald-900/40 flex flex-col justify-between space-y-4">
          <div>
            <div className="text-emerald-400 font-bold uppercase tracking-wider text-sm flex items-center gap-2 mb-3">
              <Award className="w-4 h-4" /> Armored Vehicle Hangar
            </div>

            <div className="space-y-2">
              {Object.values(TANK_CLASSES).map((tank) => (
                <button
                  key={tank.id}
                  onClick={() => setSelectedTankClass(tank.id)}
                  className={`w-full text-left p-3 rounded border text-xs font-mono transition-all ${
                    selectedTankClass === tank.id
                      ? 'bg-emerald-950/60 border-emerald-500 text-zinc-100'
                      : 'bg-zinc-900/40 border-zinc-800 text-zinc-400 hover:bg-zinc-800/40'
                  }`}
                >
                  <div className="font-bold text-sm text-zinc-200">{tank.name}</div>
                  <div className="text-[11px] text-zinc-400">{tank.role} &bull; {tank.maxSpeedKmh} km/h</div>
                </button>
              ))}
            </div>

            {/* Selected Tank Specs */}
            <div className="mt-4 pt-4 border-t border-zinc-800 space-y-2 text-xs font-mono">
              <div className="text-zinc-400 text-[11px] leading-relaxed">
                {currentTank.description}
              </div>

              <div className="space-y-1.5 pt-2">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Armor Protection:</span>
                  <span className="text-emerald-400">{currentTank.armorRating} / 10</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">125mm Firepower:</span>
                  <span className="text-emerald-400">{currentTank.firepowerRating} / 10</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Track Mobility:</span>
                  <span className="text-emerald-400">{currentTank.mobilityRating} / 10</span>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={onOpenSettings}
            className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 rounded flex items-center justify-center gap-2 text-xs uppercase font-bold tracking-wider transition-all"
          >
            <SettingsIcon className="w-3.5 h-3.5" /> Simulation Settings
          </button>
        </div>
      </div>

      {/* Bottom Footer & System telemetry */}
      <div className="border-t border-zinc-800 pt-3 flex flex-col sm:flex-row justify-between items-center text-xs text-zinc-500 font-mono gap-2">
        <div>3dtanks.hoangvv.asia &bull; Server-Authoritative 30Hz Simulation &bull; WebGL & Three.js</div>
        <div>Desktop Controls: [W/S] Throttle &bull; [A/D] Steer &bull; [Mouse] Aim &bull; [LMB] Fire &bull; [C] Cam</div>
      </div>
    </div>
  );
};
